import { useState, useEffect, useRef, type FC } from "react"
import { Link } from "react-router"
import { useLocation, useNavigate } from "react-router"
import { useAuth } from "@/lib/auth/auth-context"
import { useOrganization } from "@/lib/organization/organization-context"
import { useEstimates } from "@/lib/api/use-estimates"
import { useTasks } from "@/lib/api/use-tasks"
import { Roles, type Role } from "@/lib/team/roles"
import { useCurrentRole } from "@/lib/team/use-current-role"
import { useSetupTasks } from "@/lib/setup-tasks/use-setup-tasks"
import {
  HomeLine, FileCheck02, Car01,
  BarChart10, BankNote03, User01, Settings01,
  LogOut01, Inbox01, CalendarDate, Zap, Gift01, CheckDone01,
  // HIDE_SCHED_INC: CalendarDate (Scheduling) — re-add to the import above
  // when restoring this nav entry.
} from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { TeamSwitcher } from "./team-switcher"

function Avatar({ initials, size = "sm", className }: { initials: string; size?: "sm" | "md"; className?: string }) {
  const s = size === "sm" ? "size-8 text-xs" : "size-10 text-sm"
  return <div className={cx(s, "rounded-full bg-zinc-200 text-zinc-600 font-medium flex items-center justify-center shrink-0", className)}>{initials}</div>
}

interface NavItem {
  name: string
  href: string
  icon: FC<{ className?: string; "aria-hidden"?: boolean; strokeWidth?: number }>
  roles: readonly Role[] | null
  /** Restrict to a team context (e.g. Payouts is a body-shop function).
   *  Unlike `roles`, this is contextual — admins don't bypass it. */
  teamKinds?: readonly ("dealership" | "bodyshop")[]
  badge?: number
}

// Title-Case role values match the wire format. Bodyshop GMs hold
// `Roles.GeneralManager`, so any entry that includes GeneralManager covers
// them — the historical body_shop_manager role no longer exists.
const NAV: NavItem[] = [
  { name: "Dashboard",      href: "/dashboard",       icon: HomeLine,       roles: null },
  // The phone screen — where a body-shop advisor works the day's follow-ups.
  { name: "Tasks",          href: "/tasks",            icon: CheckDone01,    roles: null, teamKinds: ["bodyshop"] },
  { name: "Estimates",      href: "/estimates",        icon: FileCheck02,    roles: null },
  { name: "Inbox",          href: "/inbox",            icon: Inbox01,        roles: null },
  { name: "Calendar",       href: "/calendar",         icon: CalendarDate,   roles: null },
  { name: "Customers",      href: "/customers",        icon: User01,         roles: null },
  { name: "Marketing",      href: "/marketing",        icon: Zap,            roles: null },
  { name: "Referrals",      href: "/referrals",        icon: Gift01,         roles: null },
  // HIDE_SCHED_INC: Scheduling hidden — features not ready. See HideSchedulingIncentives.md.
  // { name: "Scheduling",     href: "/scheduling",       icon: CalendarDate,   roles: [Roles.Estimator, Roles.ServiceAdvisor, Roles.ServiceManager, Roles.GeneralManager, Roles.Director] },
  { name: "Total Loss",     href: "/total-loss",       icon: Car01,          roles: [Roles.Director, Roles.GeneralManager, Roles.ServiceManager, Roles.ServiceAdvisor, Roles.SalesManager, Roles.SalesRepresentative] },
  { name: "Reports",        href: "/reports",          icon: BarChart10,     roles: [Roles.Director, Roles.GeneralManager, Roles.ServiceManager] },
  // Payouts are settled by the collision center funding the rewards
  // program — body-shop manager only, never the dealership side.
  { name: "Payouts",        href: "/payouts",          icon: BankNote03,     roles: [Roles.GeneralManager], teamKinds: ["bodyshop"] },
  // "Team" was a second door to Settings → Members & invites (the /team route
  // is a legacy redirect there) — one destination, one nav entry.
]

const initials = (n: string) => n.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)

export function AppSidebar() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const { activeTeamId, activeTeamKind, availableGroups, availableDealerships, availableBodyShops } =
    useOrganization()

  const [expanded, setExpanded] = useState(false)
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mouseInside = useRef(false)
  // Pin the sidebar open while the team-switcher popover is up. Without
  // this, moving the mouse into the popover (which is portaled outside the
  // <aside>) fires mouseLeave on the aside and the sidebar collapses
  // behind the popover — a jarring snap when the popover then closes.
  const [popoverOpen, setPopoverOpen] = useState(false)

  const { data: allEstimates } = useEstimates(activeTeamId)
  const pendingCount = allEstimates?.filter(e => e.status === "Pending").length ?? 0
  // Tasks badge = work owed NOW (overdue + due). It reaches her without her
  // opening the screen to go looking — that's the whole point.
  const { data: tasks } = useTasks(activeTeamKind === "bodyshop" ? activeTeamId : null)
  const tasksBadge = (tasks?.counts.overdue ?? 0) + (tasks?.counts.now ?? 0)

  const currentRole = useCurrentRole()
  const { hasPending: hasPendingSetupTasks } = useSetupTasks()

  const hasAnyTeam =
    availableGroups.length + availableDealerships.length + availableBodyShops.length > 0

  const handleMouseEnter = () => {
    mouseInside.current = true
    if (collapseTimer.current) {
      clearTimeout(collapseTimer.current)
      collapseTimer.current = null
    }
    // Snap-open feels right; collapse stays delayed below.
    setExpanded(true)
  }

  const handleMouseLeave = () => {
    mouseInside.current = false
    // Pin the sidebar while the popover is up — the open→closed effect
    // below schedules collapse if appropriate.
    if (popoverOpen) return
    setExpanded(false)
  }

  // Track the previous popoverOpen so the collapse timer only schedules on
  // an open→closed transition. Without this, the effect would also run on
  // mount and on re-renders that don't change popoverOpen.
  const prevPopoverOpen = useRef(false)

  useEffect(() => {
    const wasOpen = prevPopoverOpen.current
    prevPopoverOpen.current = popoverOpen
    if (!(wasOpen && !popoverOpen)) return
    // If the mouse came back into the sidebar before the popover closed,
    // there's nothing to do — handleMouseEnter has already kept it open.
    if (mouseInside.current) return
    collapseTimer.current = setTimeout(() => {
      // Re-check at fire time: if the user moved back into the sidebar
      // during the 220ms grace, leave it expanded.
      if (mouseInside.current) return
      setExpanded(false)
    }, 220)
    return () => {
      if (collapseTimer.current) {
        clearTimeout(collapseTimer.current)
        collapseTimer.current = null
      }
    }
  }, [popoverOpen])

  useEffect(() => {
    return () => {
      if (collapseTimer.current) clearTimeout(collapseTimer.current)
    }
  }, [])

  const handleSignOut = async () => {
    await signOut()
    navigate("/login")
  }

  const items = NAV.filter(i => !i.teamKinds || (activeTeamKind != null && i.teamKinds.includes(activeTeamKind)))
    .filter(i => !i.roles || user?.admin || (currentRole && i.roles.includes(currentRole)))
    .map(i => i.name === "Estimates" ? { ...i, badge: pendingCount } : i.name === "Tasks" ? { ...i, badge: tasksBadge } : i)

  const isActive = (href: string) => {
    if (pathname === href) return true
    const childMatch = pathname.startsWith(`${href}/`)
    if (!childMatch) return false
    const hasMoreSpecific = items.some(
      item => item.href !== href && item.href.startsWith(`${href}/`) && (pathname === item.href || pathname.startsWith(`${item.href}/`))
    )
    return !hasMoreSpecific
  }

  return (
    <aside
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cx(
        "fixed top-0 left-0 h-screen bg-white flex flex-col z-40 border-r border-zinc-100",
        "transition-[width,box-shadow] duration-200 ease-out",
        expanded ? "w-[240px] shadow-xl shadow-zinc-200/40" : "w-[72px]",
      )}
    >
      <div className={cx("shrink-0 pt-5 pb-4", expanded ? "px-4" : "flex justify-center")}>
        <Link to="/dashboard" className="flex items-center gap-3 group">
          <div className="size-10 rounded-[14px] bg-zinc-900 grid place-items-center shrink-0 group-hover:scale-105 group-active:scale-95 transition-transform duration-200">
            <span className="text-[11px] font-black text-white tracking-tight" style={{ fontFamily: "var(--font-syne)" }}>
              e<span style={{ color: "#1D9E75" }}>X</span>
            </span>
          </div>
          {expanded && (
            <span className="text-[15px] font-black text-zinc-900 tracking-tight whitespace-nowrap" style={{ fontFamily: "var(--font-syne)" }}>
              eT<span style={{ color: "#1D9E75" }}>X</span>
              <span className="text-zinc-300 mx-0.5">·</span>
              Impa<span style={{ color: "#1D9E75" }}>X</span>t
            </span>
          )}
        </Link>
      </div>

      <div className={cx("h-px bg-zinc-100 shrink-0 mx-4")} />

      {/* Team switcher — single hierarchical popover replacing the previous
          stack of widgets (group label / All Dealerships toggle / native
          select / body-shop pills). The switcher decides internally whether
          to render a picker (multi-team user) or a static current-team-and-
          role card (single-team user). */}
      {hasAnyTeam && (
        <div className={cx("shrink-0 px-3 py-2", !expanded && "flex justify-center")}>
          <TeamSwitcher
            expanded={expanded}
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
          />
        </div>
      )}

      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 pt-2 py-1">
        <div className="flex flex-col gap-1">
          {items.map(item => {
            const active = isActive(item.href)
            return (
              <div key={item.name} className="relative group/nav">
                <Link
                  to={item.href}
                  className={cx(
                    "relative flex items-center h-11 rounded-xl outline-none transition-all duration-200 ease-out",
                    expanded ? "gap-3 px-3" : "justify-center",
                    active
                      ? "text-zinc-900"
                      : "text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50 active:scale-95",
                  )}
                  aria-current={active ? "page" : undefined}
                >
                  <item.icon className="size-6 shrink-0" strokeWidth={active ? 2 : 1.6} aria-hidden />
                  {expanded && <span className={cx("text-sm whitespace-nowrap", active ? "font-semibold" : "font-medium")}>{item.name}</span>}

                  {item.badge != null && item.badge > 0 && !expanded && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full text-[9px] font-bold bg-red-500 text-white ring-2 ring-white">
                      {item.badge}
                    </span>
                  )}
                  {item.badge != null && item.badge > 0 && expanded && (
                    <span className="ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-[10px] font-bold bg-red-500 text-white">
                      {item.badge}
                    </span>
                  )}
                </Link>

                {!expanded && (
                  <span className={cx(
                    "absolute -bottom-0.5 left-1/2 -translate-x-1/2 size-1 rounded-full transition-all duration-200",
                    active ? "bg-zinc-900 opacity-100" : "opacity-0",
                  )} />
                )}

                {!expanded && <Tooltip>{item.name}</Tooltip>}
              </div>
            )
          })}
        </div>
      </nav>

      <div className="shrink-0 flex flex-col gap-1 px-3 pb-5">
        <div className="h-px bg-zinc-100 mb-2 mx-1" />

        <div className="relative group/nav">
          <Link
            to="/settings"
            className={cx(
              "relative flex items-center h-11 rounded-xl outline-none transition-all duration-200 ease-out",
              expanded ? "gap-3 px-3" : "justify-center",
              isActive("/settings")
                ? "text-zinc-900"
                : "text-zinc-400 hover:text-zinc-600 hover:bg-zinc-50 active:scale-95",
            )}
          >
            <Settings01 className="size-6 shrink-0" strokeWidth={isActive("/settings") ? 2 : 1.6} aria-hidden />
            {expanded && <span className={cx("text-sm whitespace-nowrap", isActive("/settings") ? "font-semibold text-zinc-900" : "font-medium text-zinc-600")}>Settings</span>}
            {hasPendingSetupTasks && !expanded && (
              <span
                className="absolute top-2 right-2 size-2 rounded-full bg-emerald-500 ring-2 ring-white"
                aria-label="Setup tasks pending"
              />
            )}
            {hasPendingSetupTasks && expanded && (
              <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
                <span className="size-1.5 rounded-full bg-emerald-500" aria-hidden /> Setup
              </span>
            )}
          </Link>
          {!expanded && <Tooltip>Settings{hasPendingSetupTasks ? " · setup tasks" : ""}</Tooltip>}
        </div>

        <div className="relative group/nav">
          <button
            onClick={handleSignOut}
            className={cx(
              "flex items-center h-11 rounded-xl transition-all duration-200 outline-none w-full",
              "text-zinc-400 hover:text-red-500 hover:bg-red-50/60 active:scale-95",
              expanded ? "gap-3 px-3" : "justify-center",
            )}
          >
            <LogOut01 className="size-6 shrink-0" strokeWidth={1.6} aria-hidden />
            {expanded && <span className="text-sm font-medium whitespace-nowrap">Sign out</span>}
          </button>
          {!expanded && <Tooltip>Sign out</Tooltip>}
        </div>

        <div className="w-full h-px bg-zinc-100 my-1" />

        <div className={cx(
          "flex items-center py-2 rounded-xl",
          expanded ? "gap-3 px-3" : "justify-center",
        )}>
          <div className="relative shrink-0">
            <Avatar initials={user ? initials(user.name) : "U"} size="sm" className="shrink-0" />
            <span className="absolute -bottom-px -right-px size-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
          </div>
          {expanded && user && (
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-zinc-900 truncate">{user.name}</p>
              <p className="text-[11px] text-zinc-400 truncate">
                {user.admin ? "Admin" : currentRole ?? ""}
              </p>
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}

function Tooltip({ children }: { children: React.ReactNode }) {
  return (
    <div className={cx(
      "absolute left-full ml-4 top-1/2 -translate-y-1/2 z-50 pointer-events-none",
      "px-3 py-1.5 rounded-lg",
      "bg-white text-zinc-700 text-[13px] font-medium",
      "shadow-lg shadow-zinc-200/50 border border-zinc-100",
      "opacity-0 invisible translate-x-0.5",
      "group-hover/nav:opacity-100 group-hover/nav:visible group-hover/nav:translate-x-0",
      "transition-all duration-150 ease-out",
    )}>
      {children}
    </div>
  )
}
